import { copyToClipboard } from '@xver/utils/clipboard';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import type { CSSProperties, FocusEvent, InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../utils/classname';
import './InputField.scss';

function EyeIcon({ visible }: { visible: boolean }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1em"
            height="1em"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
        >
            {visible ? (
                <>
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                </>
            ) : (
                <>
                    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                    <path d="M6.27 6.27A12.11 12.11 0 0 0 2 12s3 7 10 7a9.9 9.9 0 0 0 5.73-1.77" />
                    <line x1="2" x2="22" y1="2" y2="22" />
                </>
            )}
        </svg>
    );
}

type InputFieldSize = 'sm' | 'default' | 'lg' | 'small' | 'medium' | 'large' | 'md';

type ResolvedInputFieldSize = 'sm' | 'default' | 'lg';

const sizeStyles: Record<
    ResolvedInputFieldSize,
    Pick<CSSProperties, 'padding' | 'fontSize' | 'height'>
> = {
    sm: {
        padding: '0 0.5rem',
        fontSize: '0.8125rem',
        height: '2rem',
    },
    default: {
        padding: '0 0.65rem',
        fontSize: '0.875rem',
        height: '2.35rem',
    },
    lg: {
        padding: '0 0.9rem',
        fontSize: '1rem',
        height: '3rem',
    },
};

function resolveInputFieldSize(size: InputFieldSize): ResolvedInputFieldSize {
    if (size === 'small') return 'sm';
    if (size === 'medium' || size === 'md') return 'default';
    if (size === 'large') return 'lg';
    return size;
}

export interface IInputFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
    size?: InputFieldSize;

    label?: ReactNode;
    labelClassName?: string;
    labelStyle?: CSSProperties;
    labelRight?: ReactNode;

    leftIcon?: ReactNode;
    rightIcon?: ReactNode;

    borderRadius?: CSSProperties['borderRadius'];
    borderWidth?: CSSProperties['borderWidth'];
    borderColor?: CSSProperties['borderColor'];
    borderStyle?: CSSProperties['borderStyle'];

    bgColor?: CSSProperties['backgroundColor'];
    backgroundColor?: CSSProperties['backgroundColor'];
    textColor?: CSSProperties['color'];
    placeholderColor?: CSSProperties['color'];
    focusBorderColor?: CSSProperties['borderColor'];

    padding?: CSSProperties['padding'];
    margin?: CSSProperties['margin'];
    gap?: CSSProperties['gap'];

    width?: CSSProperties['width'];
    height?: CSSProperties['height'];

    fontSize?: CSSProperties['fontSize'];
    fontWeight?: CSSProperties['fontWeight'];

    error?: ReactNode;

    description?: ReactNode;
    descriptionBottom?: ReactNode;

    containerClassName?: string;
    containerStyle?: CSSProperties;
    inputClassName?: string;

    passwordReveal?: boolean;
    showCopy?: boolean;
}

const InputField = forwardRef<HTMLInputElement, IInputFieldProps>(function InputField(
    {
        size = 'default',
        label,
        labelClassName,
        labelStyle,
        labelRight,
        leftIcon,
        rightIcon,
        className,
        style,
        type = 'text',
        borderRadius = '6px',
        borderWidth = '1px',
        borderColor,
        borderStyle = 'solid',
        bgColor,
        backgroundColor,
        textColor,
        placeholderColor,
        focusBorderColor,
        padding,
        margin,
        gap,
        width,
        height,
        fontSize,
        fontWeight,
        containerClassName,
        containerStyle,
        inputClassName,
        error,
        description,
        descriptionBottom,
        passwordReveal = false,
        showCopy = false,
        onFocus,
        onBlur,
        onChange,
        disabled,
        ...inputProps
    },
    ref
) {
    const { t } = useTranslation('common');
    const [focused, setFocused] = useState(false);
    const [announceError, setAnnounceError] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [copied, setCopied] = useState(false);
    const copyTimerRef = useRef<number | null>(null);
    const innerRef = useRef<HTMLInputElement | null>(null);

    const isPasswordType = type === 'password';
    const resolvedType = isPasswordType && showPassword ? 'text' : type;

    // Associate the label, description(s) and error with the input. A caller-supplied id still
    // wins, and its own aria-describedby is preserved alongside ours.
    const autoId = useId();
    const {
        id: providedId,
        'aria-describedby': providedDescribedBy,
        ...restInputProps
    } = inputProps;
    const inputId = providedId ?? autoId;
    const descriptionId = `${inputId}-description`;
    const descriptionBottomId = `${inputId}-description-bottom`;
    const errorId = `${inputId}-error`;
    const describedBy =
        [
            providedDescribedBy,
            description ? descriptionId : null,
            descriptionBottom ? descriptionBottomId : null,
            error ? errorId : null,
        ]
            .filter(Boolean)
            .join(' ') || undefined;

    const sizeStyle = sizeStyles[resolveInputFieldSize(size)];

    const resolvedPadding = padding ?? sizeStyle.padding;
    const resolvedHeight = height ?? sizeStyle.height;
    const resolvedFontSize = fontSize ?? sizeStyle.fontSize;

    const resolvedBackgroundColor =
        bgColor ??
        backgroundColor ??
        'var(--color-input-bg, color-mix(in srgb, var(--color-background) 97%, var(--color-button-bg)))';

    const resolvedTextColor = textColor ?? 'var(--color-text, currentColor)';
    const resolvedBorderColor = borderColor ?? 'var(--color-input-border, var(--color-border))';
    const resolvedFocusBorderColor = focusBorderColor ?? 'var(--color-accent)';

    const inputBaseStyle: CSSProperties & {
        '--input-placeholder-color'?: string;
    } = {
        flex: 1,
        alignSelf: 'stretch',
        width: '100%',
        minWidth: 0,
        outline: 'none',
        border: 'none',
        background: 'transparent',
        color: resolvedTextColor,
        fontSize: resolvedFontSize,
        fontWeight,
        cursor: disabled ? 'not-allowed' : 'text',
        opacity: disabled ? 0.6 : 1,
        ...(placeholderColor ? { '--input-placeholder-color': placeholderColor } : {}),
    };

    const containerStyleResolved: CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        width: width ?? '100%',
        height: resolvedHeight,
        margin,
        gap: gap ?? '0.35rem',
        padding: resolvedPadding,
        borderRadius,
        borderWidth,
        borderStyle,
        borderColor: error
            ? 'var(--color-error, #ef4444)'
            : focused
              ? resolvedFocusBorderColor
              : resolvedBorderColor,
        backgroundColor: resolvedBackgroundColor,
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        boxShadow: error
            ? `0 0 0 2px color-mix(in srgb, var(--color-error, #ef4444) 30%, transparent)`
            : focused
              ? `0 0 0 2px color-mix(in srgb, ${resolvedFocusBorderColor} 30%, transparent)`
              : undefined,
        opacity: disabled ? 0.7 : undefined,
        ...containerStyle,
    };

    const handleFocus = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            setFocused(true);
            onFocus?.(event);
        },
        [onFocus]
    );

    const handleBlur = useCallback(
        (event: FocusEvent<HTMLInputElement>) => {
            setFocused(false);
            onBlur?.(event);
        },
        [onBlur]
    );

    const handleMouseEnter = useCallback(() => setHovered(true), []);
    const handleMouseLeave = useCallback(() => setHovered(false), []);

    // merge forwarded ref with internal ref so we can read value for copy
    const setInputRef = (node: HTMLInputElement | null) => {
        innerRef.current = node;
        if (!ref) return;
        if (typeof ref === 'function') ref(node);
        else (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    };

    const handleCopy = async () => {
        try {
            const textToCopy =
                innerRef.current?.value ??
                (inputProps as any).value ??
                (inputProps as any).defaultValue ??
                '';
            if (!textToCopy) return;
            await copyToClipboard(textToCopy);
            setCopied(true);
            if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
            copyTimerRef.current = window.setTimeout(() => setCopied(false), 1500);
        } catch {
            // ignore
        }
    };

    useEffect(() => {
        return () => {
            if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
        };
    }, []);

    useEffect(() => setAnnounceError(focused), [focused]);

    const labelElement = label ? (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.5rem',
            }}
        >
            <label
                htmlFor={inputId}
                className={cn('xv-inputfield-label', labelClassName)}
                style={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'var(--color-text, currentColor)',
                    ...labelStyle,
                }}
            >
                {label}
            </label>
            {labelRight ? (
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>{labelRight}</span>
            ) : null}
        </div>
    ) : null;

    return (
        <div
            className={cn('xv-inputfield-root', containerClassName)}
            style={{ display: 'inline-flex', flexDirection: 'column', width: width ?? '100%' }}
        >
            {labelElement}
            {description && (
                <span
                    id={descriptionId}
                    className="xv-inputfield-description"
                    style={{
                        fontSize: '0.85rem',
                        color: 'color-mix(in srgb, var(--color-text) 68%, transparent)',
                        marginBottom: '0.25rem',
                    }}
                >
                    {description}
                </span>
            )}
            <div
                className={cn('xv-inputfield-wrapper', className)}
                style={containerStyleResolved}
                onMouseDown={(event) => {
                    if (event.target !== event.currentTarget || disabled) return;
                    event.preventDefault();
                    innerRef.current?.focus();
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {leftIcon && (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            color: 'var(--color-text-muted, currentColor)',
                        }}
                    >
                        {leftIcon}
                    </span>
                )}
                <input
                    ref={setInputRef}
                    id={inputId}
                    type={resolvedType}
                    disabled={disabled}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy}
                    className={cn('xv-inputfield', inputClassName)}
                    style={{ ...inputBaseStyle, ...style }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onChange={onChange}
                    {...restInputProps}
                />
                {isPasswordType && passwordReveal && (
                    <button
                        type="button"
                        tabIndex={-1}
                        disabled={disabled}
                        onClick={() => setShowPassword((v) => !v)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            padding: 0,
                            margin: 0,
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--color-text-muted, currentColor)',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.5 : 0.7,
                            transition: 'opacity 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                            if (!disabled) e.currentTarget.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = disabled ? '0.5' : '0.7';
                        }}
                        aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                    >
                        <EyeIcon visible={showPassword} />
                    </button>
                )}
                {showCopy && (
                    <button
                        type="button"
                        tabIndex={-1}
                        disabled={disabled}
                        onClick={handleCopy}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            padding: 0,
                            margin: 0,
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--color-text-muted, currentColor)',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            opacity: disabled ? 0.5 : 0.7,
                            transition: 'opacity 0.15s ease',
                        }}
                        onMouseEnter={(e) => {
                            if (!disabled) e.currentTarget.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = disabled ? '0.5' : '0.7';
                        }}
                        aria-label={copied ? t('copied') : t('copy')}
                    >
                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </button>
                )}
                {rightIcon && (
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            color: 'var(--color-text-muted, currentColor)',
                        }}
                    >
                        {rightIcon}
                    </span>
                )}
            </div>
            {descriptionBottom && (
                <span
                    id={descriptionBottomId}
                    className="xv-inputfield-description-bottom"
                    style={{
                        fontSize: '0.85rem',
                        color: 'color-mix(in srgb, var(--color-text) 68%, transparent)',
                        marginTop: '0.25rem',
                    }}
                >
                    {descriptionBottom}
                </span>
            )}
            <span
                id={errorId}
                aria-live={announceError ? 'polite' : 'off'}
                style={{
                    fontSize: '0.75rem',
                    color: 'var(--color-error, #ef4444)',
                    marginTop: error ? '0.25rem' : undefined,
                }}
            >
                {error}
            </span>
        </div>
    );
});

export default InputField;
